import { TimeTrackingReportRow, TimeTrackingReportSubTaskRow } from '../data/types';

// Exact header row required by the spec (and matching the supplied sample file).
//
// Columns A-I preserve their positions and values; the explicit requirement renames A from the
// ambiguous "Date" to "Work Date". New fields append after the existing Sub-task column so no
// established data column moves. `scripts/verify-report-workbook.mjs` gates this.
const HEADERS = [
    'Work Date',
    'Client',
    'Project ID',
    'Project Name',
    'Design Type',
    'Designer Name',
    'Report Type',
    'Total time (hh:mm)',
    'Comments',
    'Sub-task',
    'Created Datetime',
];

// Column widths roughly matching the sample workbook.
const COLUMN_WIDTHS = [13, 56, 24, 52, 29, 23, 18, 16, 80, 22, 22];

// Sheet 2 — the eleven contract columns, in contract order.
const SUB_TASK_SHEET_NAME = 'Sub-task Breakdown';
const SUB_TASK_HEADERS = [
    'Parent Request',
    'Sub Task',
    'Scope summary',
    'Assigned Designer',
    'Logging Designer',
    'Phase status',
    'Due Date',
    'Work Date',
    'Report Type',
    'Duration',
    'Entry Count',
];
const SUB_TASK_COLUMN_WIDTHS = [52, 22, 60, 23, 23, 16, 13, 13, 18, 16, 13];

// Parse a 'YYYY-MM-DD' string into a timezone-safe Date at UTC midnight.
// Using Date.UTC avoids the off-by-one shift ExcelJS would otherwise produce
// from the local timezone offset (ExcelJS derives the serial from getTime()).
const parseWorkDate = (workDate: string | null): Date | null => {
    if (!workDate) {
        return null;
    }

    const parts = workDate.split('-');
    if (parts.length !== 3) {
        return null;
    }

    const [year, month, day] = parts.map((p) => parseInt(p, 10));
    if (!year || !month || !day) {
        return null;
    }

    return new Date(Date.UTC(year, month - 1, day));
};

// Builds the .xlsx as a Blob using ExcelJS (lazy-loaded to keep it out of the main bundle).
export const buildWorkbookBlob = async (
    rows: TimeTrackingReportRow[],
    subTaskRows: TimeTrackingReportSubTaskRow[] = []
): Promise<Blob> => {
    const ExcelJSModule = await import('exceljs');
    // exceljs is published as CommonJS; with esModuleInterop the namespace may be under .default.
    const ExcelJS: any = (ExcelJSModule as any).default ?? ExcelJSModule;

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('User Activity Report');

    // Header row (bold)
    const headerRow = sheet.addRow(HEADERS);
    headerRow.font = { bold: true };

    // Column widths
    COLUMN_WIDTHS.forEach((width, index) => {
        sheet.getColumn(index + 1).width = width;
    });

    // Data rows
    rows.forEach((row) => {
        const addedRow = sheet.addRow([
            parseWorkDate(row.work_date), // A: Work Date (real date)
            row.client_name ?? '', // B: Client
            row.project_id ?? '', // C: Project ID (text)
            row.project_name ?? '', // D: Project Name
            row.design_type ?? '', // E: Design Type
            row.designer_name ?? '', // F: Designer Name
            row.report_type ?? '', // G: Report Type
            (row.duration_min ?? 0) / 1440, // H: Total time as an Excel time fraction
            row.comment ?? '', // I: Comments
            row.sub_task_label ?? '', // J: Sub-task ('' for work logged on the parent Request)
            new Date(row.created_datetime), // K: Created Datetime (real instant)
        ]);

        // A: short date format (matches sample numFmt 14)
        addedRow.getCell(1).numFmt = 'mm-dd-yy';
        // C: force text so long snowflake IDs are not rounded to scientific notation
        addedRow.getCell(3).numFmt = '@';
        // H: hours:minutes (matches sample numFmt 20)
        addedRow.getCell(8).numFmt = 'h:mm';
        // K: creation instant, distinct from the selected Work Date in A
        addedRow.getCell(11).numFmt = 'mm-dd-yy h:mm';
    });

    // Sheet 2 is omitted entirely when there is no breakdown, so a report over a range with no
    // sub-ticket activity is byte-comparable in shape to a pre-sub-ticketing report.
    if (subTaskRows.length > 0) {
        const subTaskSheet = workbook.addWorksheet(SUB_TASK_SHEET_NAME);

        const subTaskHeaderRow = subTaskSheet.addRow(SUB_TASK_HEADERS);
        subTaskHeaderRow.font = { bold: true };

        SUB_TASK_COLUMN_WIDTHS.forEach((width, index) => {
            subTaskSheet.getColumn(index + 1).width = width;
        });

        subTaskRows.forEach((row) => {
            const addedRow = subTaskSheet.addRow([
                row.parent_request ?? '', // A: Parent Request
                row.sub_task ?? '', // B: Sub Task (matches sheet 1 column J exactly)
                row.scope_summary ?? '', // C: Scope summary
                row.assigned_designer ?? '', // D: Assigned Designer
                row.logging_designer ?? '', // E: Logging Designer
                row.phase_status ?? '', // F: Phase status
                parseWorkDate(row.due_date) ?? '', // G: Due Date
                // H: a planned phase with no time logged has no work date at all
                parseWorkDate(row.work_date) ?? '', // H: Work Date
                row.report_type ?? '', // I: Report Type
                (row.duration_min ?? 0) / 1440, // J: Duration as an Excel time fraction
                row.entry_count ?? 0, // K: Entry Count
            ]);

            // A: force text so the parent label keeps its exact snowflake ID
            addedRow.getCell(1).numFmt = '@';
            addedRow.getCell(7).numFmt = 'mm-dd-yy';
            addedRow.getCell(8).numFmt = 'mm-dd-yy';
            // J: an unstarted phase renders as 0:00 rather than as a blank
            addedRow.getCell(10).numFmt = 'h:mm';
        });
    }

    const buffer = await workbook.xlsx.writeBuffer();

    return new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
};

// Triggers a browser download of the given blob.
export const downloadBlob = (blob: Blob, fileName: string): void => {
    const url = window.URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    window.URL.revokeObjectURL(url);
};
